import ChangePasswordModal from "./ChangePasswordModal";
import EditProfileModal from "./EditProfileModal";
import TwoFactorAuthentication from "./TwoFactorAuthentication";
import CustomToolTip from "components/tooltips";

const General = ({
  profileData, fetchProfile, data2FA, fetch2fa,
}: {
  profileData: any; data2FA?: any; fetch2fa?: () => void; fetchProfile?: () => void;
}) => {
  const fields = [
    { label: "First Name", value: profileData?.first_name, icon: "👤", color: "blue"   },
    { label: "Last Name",  value: profileData?.last_name,  icon: "👤", color: "blue"   },
    { label: "Email",      value: profileData?.email,      icon: "✉️",  color: "purple" },
    { label: "Role",       value: profileData?.role,       icon: "🔖", color: "amber"  },
  ];

  const colorMap: Record<string, { bg: string; border: string; dot: string }> = {
    blue:   { bg: "linear-gradient(135deg,#eff6ff,#dbeafe)", border: "#bfdbfe", dot: "#2563eb" },
    purple: { bg: "linear-gradient(135deg,#fdf4ff,#fae8ff)", border: "#e9d5ff", dot: "#7c3aed" },
    amber:  { bg: "linear-gradient(135deg,#fffbeb,#fef3c7)", border: "#fde68a", dot: "#d97706" },
  };

  return (
    <div className="gen-card">
      <div className="gen-header">
        <div className="gen-header-icon">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
          </svg>
        </div>
        <div>
          <h3 className="gen-title">General Information</h3>
          <p className="gen-sub">Your account details</p>
        </div>
      </div>

      <div className="gen-fields">
        {fields.map((f) => {
          const c = colorMap[f.color] ?? colorMap.blue;
          return (
            <div key={f.label} className="gen-field-card">
              <div className="gen-field-dot" style={{ background: c.dot }} />
              <div className="gen-field-inner">
                <p className="gen-field-label">{f.label}</p>
                <p className="gen-field-value">
                  {f.label === "Email" && f.value ? (
                    <CustomToolTip
                      content={f.value}
                      trigger={f.value.length > 22 ? f.value.substring(0, 22) + "…" : f.value}
                      header=""
                    />
                  ) : (
                    f.value || "—"
                  )}
                </p>
              </div>
            </div>
          );
        })}
      </div>

      <div className="gen-actions">
        <div className="gen-actions-label">Account Actions</div>
        <div className="gen-actions-row">
          <EditProfileModal data={profileData} fetchProfile={fetchProfile} />
          <ChangePasswordModal />
          <TwoFactorAuthentication data={data2FA} profileData={profileData} fetch2fa={fetch2fa} fetchProfile={fetchProfile} />
        </div>
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

        .gen-card { font-family:'Plus Jakarta Sans',sans-serif; background:#fff; border:1px solid #e2e8f0; border-radius:18px; overflow:hidden; box-shadow:0 2px 8px rgba(15,23,42,.06); }

        .gen-header { padding:20px 22px 16px; border-bottom:1px solid #f1f5f9; display:flex; align-items:center; gap:12px; }
        .gen-header-icon { width:38px; height:38px; border-radius:10px; background:linear-gradient(135deg,#eff6ff,#dbeafe); border:1.5px solid #bfdbfe; display:flex; align-items:center; justify-content:center; color:#2563eb; flex-shrink:0; }
        .gen-title { font-size:14px; font-weight:800; color:#0f172a; letter-spacing:-0.2px; margin-bottom:2px; line-height:1.2; }
        .gen-sub { font-size:12px; color:#94a3b8; font-weight:500; }

        .gen-fields { padding:16px 20px; display:grid; grid-template-columns:1fr 1fr; gap:10px; }
        .gen-field-card { background:#f8fafc; border:1.5px solid #f1f5f9; border-radius:12px; padding:12px 14px; display:flex; align-items:flex-start; gap:10px; transition:border-color .15s; min-width:0; }
        .gen-field-card:hover { border-color:#e2e8f0; }
        .gen-field-dot { width:7px; height:7px; border-radius:50%; flex-shrink:0; margin-top:6px; }
        .gen-field-inner { flex:1; min-width:0; }
        .gen-field-label { font-size:11px; font-weight:700; color:#94a3b8; text-transform:uppercase; letter-spacing:0.4px; margin-bottom:4px; }
        .gen-field-value { font-size:13.5px; font-weight:700; color:#0f172a; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; margin-top:0 !important; }

        .gen-actions { padding:14px 20px 18px; border-top:1px solid #f1f5f9; }
        .gen-actions-label { font-size:10.5px; font-weight:800; color:#b8c4d8; text-transform:uppercase; letter-spacing:0.8px; margin-bottom:10px; }
        .gen-actions-row { display:flex; align-items:center; gap:8px; flex-wrap:wrap; }

        /* ── Tablet side-by-side ── */
        @media (max-width: 768px) {
          .gen-header { padding: 16px 18px 14px; }
          .gen-fields { padding: 12px 16px; gap: 8px; }
          .gen-field-card { padding: 10px 12px; }
          .gen-field-value { font-size: 13px; }
          .gen-actions { padding: 12px 16px 16px; }
        }

        /* ── Mobile ── */
        @media (max-width: 600px) {
          .gen-card { border-radius: 16px; }
          .gen-header { padding: 14px 16px 12px; }
          .gen-header-icon { width: 34px; height: 34px; border-radius: 9px; }
          .gen-title { font-size: 13px; }
          /* Stack fields to single column on very narrow */
          .gen-fields { grid-template-columns: 1fr 1fr; padding: 12px 14px; gap: 8px; }
          .gen-field-label { font-size: 10px; }
          .gen-field-value { font-size: 12.5px; }
          .gen-actions { padding: 12px 14px 16px; }
          .gen-actions-row { gap: 6px; }
        }

        @media (max-width: 380px) {
          .gen-fields { grid-template-columns: 1fr; }
          .gen-field-card { padding: 9px 11px; }
        }
      `}</style>
    </div>
  );
};

export default General;